import { useMemo } from "react";
import { motion } from "framer-motion";

/**
 * Ambient cosmic starfield.
 *
 * Fixed-positioned behind the whole landing page. Deep-space radial background,
 * three static nebula clouds, and one twinkling star layer. NO scroll parallax
 * (that felt buggy on mobile) — just calm twinkling stars and drifting nebula.
 * Pure CSS + framer-motion, no canvas or three.js.
 */

function mulberry32(seed) {
  return () => {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function makeStars(count, seed) {
  const rand = mulberry32(seed);
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    x: rand() * 100,
    y: rand() * 100,
    size: 0.5 + rand() * 1.8,
    opacity: 0.25 + rand() * 0.6,
    twinkleDelay: rand() * 6,
    twinkleDuration: 3 + rand() * 4,
  }));
}

const STARS = makeStars(140, 42);

export default function Starfield() {
  const stars = useMemo(() => STARS, []);

  return (
    <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden" aria-hidden="true">
      {/* Deep-space background — calm, no motion */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 30%, #14082a 0%, #0a0912 55%, #000 100%)",
        }}
      />

      {/* Three static nebula clouds, faint colour wash */}
      <div
        className="absolute -top-40 -left-40 h-[900px] w-[900px] rounded-full blur-[160px] opacity-45"
        style={{ background: "radial-gradient(circle, rgba(157,78,221,0.30), transparent 60%)" }}
      />
      <div
        className="absolute top-1/3 -right-40 h-[800px] w-[800px] rounded-full blur-[140px] opacity-40"
        style={{ background: "radial-gradient(circle, rgba(255,16,122,0.22), transparent 60%)" }}
      />
      <div
        className="absolute bottom-0 left-1/4 h-[700px] w-[700px] rounded-full blur-[120px] opacity-35"
        style={{ background: "radial-gradient(circle, rgba(0,212,255,0.20), transparent 60%)" }}
      />

      {/* Twinkling stars — no scroll parallax */}
      <div className="absolute inset-0">
        {stars.map((s) => (
          <motion.span
            key={s.id}
            className="absolute rounded-full"
            style={{
              left: `${s.x}%`,
              top: `${s.y}%`,
              width: s.size,
              height: s.size,
              background: "#ffffff",
              boxShadow: `0 0 ${4 + s.size * 2}px rgba(255,255,255,0.6)`,
            }}
            animate={{ opacity: [s.opacity, s.opacity * 0.3, s.opacity] }}
            transition={{
              duration: s.twinkleDuration,
              delay: s.twinkleDelay,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      {/* Vignette keeps foreground legible */}
      <div
        className="absolute inset-0"
        style={{ background: "radial-gradient(ellipse at 50% 50%, transparent 40%, rgba(0,0,0,0.55) 100%)" }}
      />
    </div>
  );
}
